<template>
    <div class="box podcast-info">
        <h2 class="box-title">
            Podcast Info
        </h2>
        <div class="box-content">
            <form
                id="podcast-info"
                class="podcast-info-form"
                @submit.prevent="saveInfo"
            >
                <form-row
                    id="feed-url"
                    class="feed-url"
                    label="Your RSS URL"
                    :error="fields.feedUrl.error"
                >
                    <div class="feed-url-wrapper">
                        <div class="feed-url-input" :class="{ copyable: show.hosted }">
                            <form-input
                                id="feed-url"
                                v-model="fields.feedUrl.value"
                                type="text"
                                :disabled="show.hosted"
                                autocomplete="off"
                                :error="!! fields.feedUrl.error"
                                @focus="clearError('feedUrl')"
                            />

                            <button
                                v-if="show.hosted"
                                type="button"
                                class="feed-url-copy-button"
                                aria-label="Copy RSS URL to clipboard"
                                @click="onCopyFeedUrlClick"
                            >
                                <i class="far fa-copy feed-url-copy-icon" />
                            </button>
                        </div>

                        <button
                            class="btn is-gray feed-refresh-button"
                            type="button"
                            @click="onRefreshFeedClick"
>
                            Refresh Feed
                        </button>
                    </div>
                </form-row>

                <form-row
                    id="title"
                    class="title"
                    label="Podcast Title"
                    :error="fields.title.error"
                >
                    <form-input
                        id="title"
                        v-model="fields.title.value"
                        type="text"
                        autocomplete="off"
                        :error="!! fields.title.error"
                        @focus="clearError('title')"
                    />
                </form-row>

                <form-row
                    v-if="show.hosted"
                    id="description"
                    class="description"
                    label="Podcast Description"
                    :help="descriptionRemainingCharactersHelp"
                    :error="fields.description.error"
                >
                    <form-rich-editor
                        id="description"
                        v-model="fields.description.value"
                        name="description"
                        :error="!! fields.description.error"
                        @focus="clearError('description')"
                    />
                </form-row>

                <form-row
                    v-if="show.hosted"
                    class="artwork"
                    label="Episode Artwork"
                    help="(JPG or PNG, square image between 1400x1400 and 3000x3000 pixels)"
                    :error="fields.artwork.error"
                >
                    <form-image
                        id="artwork"
                        :accept="artworkAllowedExtensions"
                        select-label="Upload artwork"
                        change-label="Change artwork"
                        :preview-url="show.cover.imageUrl"
                        :error="!! fields.artwork.error"
                        @change="onArtworkChange"
                    />
                </form-row>

                <div class="controll-container flex">
                    <base-form-button
                        class="btn"
                        :loading="formProcessing"
                    >
                        Save Changes
                    </base-form-button>
                </div>
            </form>

            <hosting-call-to-action
                v-if="! show.hosted"
                screen="settings"
                :show="show"
            >
                <ul>
                    <li>All-in-one dashboard</li>
                    <li>Detailed analytics</li>
                    <li>Pain-free migration</li>
                </ul>
            </hosting-call-to-action>
        </div>
    </div>
</template>
<script>
// External dependencies.
import { find, isEqual, uniq, mapValues } from 'lodash';
import { writeToClipboard } from '@/utils/clipboard';
// Internal dependencies.
import BaseFormButton from '@/components/BaseFormButton';
import FormImage from '@/components/Form/FormImage';
import FormInput from '@/components/Form/FormInput';
import FormRichEditor from '@/components/Form/FormRichEditor';
import FormRow from '@/components/Form/FormRow';
import HostingCallToAction from '@/components/Shows/HostingCallToAction';
import toasts from '@/mixins/toasts';
import {
    validateFeedUrl,
    validateTitle,
    validateDescription,
    validateArtwork,
} from '@/utils/form/showValidation';
import htmlToText from '@/utils/htmlToText';
import {
    descriptionCharactersLimit,
    artworkAllowedTypes,
} from '@/utils/config/showOptions';

export default {
    components: {
        BaseFormButton,
        FormImage,
        FormInput,
        FormRichEditor,
        FormRow,
        HostingCallToAction,
    },

    mixins: [
        toasts,
    ],

    props: {
        show: {
            type: Object,
            default: () => ({}),
        },
        formProcessing: {
            type: Boolean,
            required: true,
        },
        onChange: {
            type: Function,
            default: null,
        },
    },

    data: () => ({
        fields: {
            feedUrl: {
                value: null,
                error: null,
            },
            title: {
                value: null,
                error: null,
            },
            description: {
                value: null,
                error: null,
            },
            artwork: {
                value: null,
                error: null,
            },
        },
    }),

    computed: {
        /**
         * Computes amount of remaining characters in description.
         *
         * @returns {number} Amount of remaining characters.
         */
        descriptionRemainingCharacters() {
            return descriptionCharactersLimit - htmlToText(this.fields.description.value || '').length;
        },

        /**
         * Computes help text for description remaining characters.
         *
         * @returns {string} Description remainig charaters help text.
         */
        descriptionRemainingCharactersHelp() {
            if (0 <= this.descriptionRemainingCharacters) {
                return `(${this.descriptionRemainingCharacters} character(s) remaining)`;
            }

            return `(over ${-this.descriptionRemainingCharacters} characters)`;
        },

        /**
         * Returns list of allowed Artwork extensions.
         *
         * @returns {string[]} List of allowed Artwork extensions.
         */
        artworkAllowedExtensions() {
            return Object.keys(artworkAllowedTypes);
        },

        /**
         * Returns list of allowed Artwork MIME types.
         *
         * @returns {string[]} List of allowed Artwork MIME types.
         */
        artworkAllowedMimeTypes() {
            return uniq(Object.values(artworkAllowedTypes));
        },
    },

    watch: {
        fields: {
            /**
             * Handles change of fields.
             *
             * @param  {object} data Updated data.
             */
            async handler(data) {
                if (! await this.validate() || this.formProcessing) {
                    return;
                }

                if (this.onChange) {
                    this.onChange(mapValues(data, 'value'));
                }
            },
            deep: true,
        },

        show: {
            immediate: true,

            /**
             * Manages data.
             *
             * @param {object} show Show object data.
             * @param {*} oldShow Show object data.
             */
            handler(show, oldShow) {
                if (isEqual(show, oldShow)) {
                    return;
                }

                this.fields.feedUrl.value = show.isValid ? show.feedUrl : 'Unknown';
                this.fields.title.value = show.isValid ? show.title : 'Unknown';

                if (this.show.hosted) {
                    this.fields.description.value = show.isValid ? show.description : 'Unknown';
                }
            },
        },
    },

    methods: {
        /**
         * Clears errors
         *
         * @param {string} field Field name.
         */
        clearError(field) {
            this.fields[field].error = null;
        },

        /**
         * Submits form
         *
         * @returns {void} Do nothing, now.
         */
        async saveInfo() {
            if (! await this.validate() || this.formProcessing) {
                return;
            }

            this.$emit('submit');
        },

        /**
         * Valdiates form and set errors
         *
         * @returns {boolean} If validation is passed
         */
        async validate() {
            this.fields.title.error = validateTitle(this.fields.title.value);
            this.fields.feedUrl.error = ! this.show.hosted ? validateFeedUrl(this.fields.feedUrl.value) : null;
            this.fields.description.error = this.show.hosted ? validateDescription(this.fields.description.value, {
                charactersLimit: descriptionCharactersLimit,
            }) : null;
            this.fields.artwork.error = this.show.hosted ? await validateArtwork(this.fields.artwork.value, {
                allowedMimeTypes: this.artworkAllowedMimeTypes,
            }) : null;

            return ! find(this.fields, field => !! field.error);
        },

        /**
         * Handles copy RSS URL button click.
         */
        async onCopyFeedUrlClick() {
            await writeToClipboard(this.fields.feedUrl.value);

            this.toastSuccess({ message: { title: 'RSS URL copied to clipboard.' } });
        },

        /**
         * Handles refresh show button click.
         */
        async onRefreshFeedClick() {
            this.$http.post(`/api/shows/${this.show.hash}/refresh`)
                .then((response) => {
                    this.toastSuccess({ message: { title: 'Your show will be refreshed within few minutes.' } });
                })
                .catch(() => {
                    this.toastError({ message: { title: 'Please wait patiently for show to refresh.' } });
                });
        },

        /**
         * Handles artwork change.
         *
         * @param  {File} file Artwork file.
         */
        onArtworkChange(file) {
            this.clearError('artwork');

            this.fields.artwork.value = file;
        },
    },
};
</script>
